import { useContext, useState, useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
  InputTextarea,
  ToggleButton,
} from "iuvity-ui-components";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { CustomInput, FormSkeleton } from "../../components";
import { useTranslations } from "../../hooks/useTranslations";
import { useWidgets } from "../../hooks/useWidgets";
import {
  REVALIDATE_FORM_PROPS,
  TRANSLATION_LABELTYPE_OPTIONS,
} from "../../constants";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useVersions } from "../../hooks/useVersions";
import { TranslationsSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { AppContext } from "../../context/index";
import { Tooltip } from "primereact/tooltip"
import { formatDateToLocale } from "../../helpers/formatDate";
import { DescriptionBox } from "../../components/pageComponents";
import { validateLabel } from "../../services/configurations/translations";
import { ValidateLabelsTable } from "../../components/translation/ValidateLabelsTable";
import noLabelsFoundedImage from "../../assets/Illustration_noApprove.svg";
import { registerAudit } from "../../helpers/registerAudit";
import { PrivilegeValidatorComponent } from "../../components/shared/PrivilegeValidatorComponent";

interface NewTranslationProps {
  update?: boolean;
  detail?: boolean;
}

const formBlankValues = {
  key: "",
  // description: "",
  envalue: "",
  esvalue: "",
  type: "",
  widgets: [],
  releaseversion: "",
};

export const NewTranslationPage: React.FC<NewTranslationProps> = ({
  update = false,
  detail = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    toast,
    appState: { user, selectedLabel, language,
      permissiondInfoByRole, selectedApp},
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
      ? {
          id: selectedLabel?.id,
          key: selectedLabel?.key || "",
          // description: selectedLabel?.description || "",
          translationenglishid: selectedLabel?.translationenglishid,
          envalue: selectedLabel?.envalue || "",
          translationspanishid: selectedLabel?.translationspanishid,
          esvalue: selectedLabel?.esvalue || "",
          type: selectedLabel?.type,
          widgets:
            selectedLabel?.widgets.map((widget) => ({
              name: widget,
              code: widget,
            })) || [],
          releaseversion: selectedLabel?.releaseversion,
          updatedby: user?.name,
        }
      : (detail
          ? {
              id: selectedLabel?.id,
              key: selectedLabel?.key || "",
              // description: selectedLabel?.description || "",
              translationenglishid: selectedLabel?.translationenglishid,
              envalue: selectedLabel?.envalue || "",
              translationspanishid: selectedLabel?.translationspanishid,
              esvalue: selectedLabel?.esvalue || "",
              type: !!selectedLabel?.type ? t(`Translations.${selectedLabel?.type?.toLowerCase()}OptionLabel`) : "",
              widgets: selectedLabel?.widgets || [],
              releaseversion: selectedLabel?.releaseversion,
              updatedby: user?.name,
            }
          : {
              ...formBlankValues,
              createdby: user?.name,
            }
      ),
  });

  const { translationAction } = useTranslations("ACTIONS");
  const { data: versionsData } = useVersions("DATA");
  const { data: widgets } = useWidgets("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [similarLabelsList, setSimilarLabelsList] = useState<any>(undefined);
  const [wasLabelValidated, setWasLabelValidated] = useState<boolean>(false);
  const [fieldsToValidateObject, setFieldsToValidateObject] = useState<{
    widgets: {
      name: string,
      code: string,
    }[];
    key: string;
  }> ({
    widgets: selectedLabel?.widgets?.map((widget) => ({
      name: widget,
      code: widget,
    })) ?? [],
    key: selectedLabel?.key ?? '',
  });

  // EFECTO PARA HABILITAR BOTÓN DE VALIDACIÓN DE
  // ETIQUETAS CUANDO EL FORMULARIO SEA DIRTY
  useEffect(()=>{
    watch((value)=>{
      if ((JSON.stringify(selectedLabel?.widgets?.map((widget) => ({
        name: widget,
        code: widget,
      })).sort((a:any,b:any)=>a.name.localeCompare(b)))
        === JSON.stringify(value.widgets?.sort((a:any,b:any)=>a.name.localeCompare(b))))
        && (selectedLabel?.key === value.key)) {
          setWasLabelValidated(false);
          setSimilarLabelsList(undefined);
          setFieldsToValidateObject({
            widgets: selectedLabel?.widgets?.map((widget) => ({
              name: widget,
              code: widget,
            })) ?? [],
            key: selectedLabel?.key ?? '',
          });
        }
      else if (!Object.values(value).includes('')
        && (Object.keys(getValues())
            .filter((labelProps:string) => ['widgets','key'].includes(labelProps) &&
              (
                labelProps === 'widgets'
                  ? (JSON.stringify((wasLabelValidated ? fieldsToValidateObject?.widgets
                    : (!!selectedLabel
                        ? selectedLabel?.widgets?.map((widget) => ({
                            name: widget,
                            code: widget,
                          }))
                        : getValues('widgets'))).sort((a:any,b:any)=>a.name.localeCompare(b)))
                    === JSON.stringify(value.widgets?.sort((a:any,b:any)=>a.name.localeCompare(b))))
                  // @ts-ignore
                  : ((!!selectedLabel ? (wasLabelValidated
                      ? fieldsToValidateObject
                      : selectedLabel).key : getValues('key')) !== value.key)
              )).length > 0)) {
                  setWasLabelValidated(false);
                  setSimilarLabelsList(undefined);
        };
    });
  },[fieldsToValidateObject, getValues, selectedLabel, wasLabelValidated, watch]);

  const widgetsValues = useWatch({
    control,
    name: "widgets",
  });

  const keyValues = useWatch({
    control,
    name: "key",
  });

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedLabel: null,
    }));
    navigate('/translations');
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await translationAction(detail
      ? "DELETE" : (update ? "UPDATE" : "CREATE"), !detail ? [
      {
        label: {
          id: formValues?.id,
          key: formValues.key,
          // description: formValues.description,
          type: formValues.type,
          // @ts-ignore
          widgets: formValues.widgets.map((widget) => widget.code),
          releaseversion: formValues?.releaseversion,
          [update ? "updatedby" : "createdby"]: user?.name,
        },
        translations: [
          {
            id: formValues?.translationspanishid,
            value: formValues.esvalue,
            languageisocode: "es",
          },
          {
            id: formValues?.translationenglishid,
            value: formValues.envalue,
            languageisocode: "en",
          },
        ],
      },
    ] : formValues?.id);
    if (actionResult === "SUCCESS") {
      navigate("/translations");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Translations.${detail ? "deleteLabel" : (update ? "save" : "create")}ModalTitle`),
          content: t(`Translations.${detail ? "deleteLabel" : (update ? "save" : "create")}ModalContent`).replace('{{labelname}}', getValues('key')),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.cancelModalTitle"),
          content: t("Translations.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update
              ? t("Translations.editTitle")
              : (detail
                  ? t("Translations.detailTitle") : t("Translations.newTitle"))}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Translations.translationsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Menu.SetupLabel"),
              disabled: true
            },
            {
              label: t("Translations.translationsTitleFront"),
              command: () => navigate('/translations'),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
                ? t("Translations.editTitle")
                : (detail
                    ? t("Translations.detailTitle") : t("Translations.newTitle")),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      {widgets && versionsData ? (
        <>
          <DescriptionBox
            label={`Translations.${update
              ? 'edit'
              : (detail ? 'detail' : 'create')}LabelDescription`}
          />
          {
            (detail && permissiondInfoByRole.UPDATE) &&
            <SubheaderActions
              className="multiactions-c"
              actions={[
                {
                  id: "111",
                  icon: "iu iu-icon-edit",
                  label: t("Translations.editTitle"),
                  onClick: () => {
                    setValue('widgets',selectedLabel?.widgets.map((widget) => ({
                      name: widget,
                      code: widget,
                    })) || []);
                    navigate('/translations/edit');
                  },
                  style: {
                    paddingRight: "var(--padding-level-twenty-four)",
                  },
                },
              ]}
            />
          }
          <BoxFull
            className="flex flex-column align-items-center justify-content-center mb-4"
            style={{ background: "transparent" }}
          >
            <div className="form-container md py-5 px-8 w-full">
              <div className="form align-items-center justify-content-center w-full">
                <div className="flex justify-content-around w-full flex-wrap">
                  <div>
                    <div className="mb-4">
                      <Controller
                        name="widgets"
                        control={control}
                        rules={TranslationsSchema(t).widgets}
                        render={({ field }) => {
                          field.onChange = (e) => {
                            // @ts-ignore
                            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                          };
                          return (
                                !detail
                                ? <>
                                    <Tooltip
                                      target=".widget-tooltip"
                                      content={t('Translations.widgetsTooltip')}
                                      position="bottom"
                                    />
                                    <div
                                      className="flex align-items-center gap-2"
                                    >
                                      <label
                                        htmlFor={"widgets"}
                                        className="block lb mt-2 mb-1"
                                      >
                                        {t("Translations.functionalityLabel")}
                                      </label>
                                      <i
                                        className="iu iu-icon-info widget-tooltip"
                                        style={{
                                          color: "var(--successColor)",
                                          marginTop: "0.25rem",
                                        }}
                                      />
                                    </div>
                                    <MultiSelect
                                      id={`select-widget`}
                                      display="chip"
                                      optionLabel="name"
                                      placeholder={t(
                                        "Translations.functionalityPlaceholderLabel"
                                      )}
                                      // className="mb-4"
                                      style={{ width: "25rem" }}
                                      options={widgets.map((widget) => ({
                                        name: widget.widgetname,
                                        code: widget.widgetname,
                                      }))}
                                      filter
                                      {...field}
                                    />
                                  </>
                                : (!detail
                                  ? <CustomInput
                                      id={`input-label-widgets`}
                                      label={t("Translations.functionalityLabel")}
                                      style={{
                                        textOverflow: 'ellipsis',
                                      }}
                                      type="text"
                                      className="input-md"
                                      hasTooltip
                                      tooltipText={t('Translations.widgetsTooltip')}
                                      isReadOnly
                                      {...field}
                                    />
                                  : <div
                                      className="flex flex-column gap-2 mt-1"
                                      style={{ marginBottom: 0, maxWidth: '295px' }}
                                    >
                                      <Tooltip
                                        target=".widget-tooltip"
                                        content={t('Translations.widgetsTooltip')}
                                        position="bottom"
                                      />
                                      <div
                                        className="flex align-items-center gap-2"
                                      >
                                        <label
                                          htmlFor={"widgets"}
                                          className="block lb mt-2"
                                        >
                                          {t("Translations.functionalityLabel")}
                                        </label>
                                        <i
                                          className="iu iu-icon-info widget-tooltip"
                                          style={{
                                            color: "var(--successColor)",
                                            marginTop: "0.25rem",
                                          }}
                                        />
                                      </div>
                                      <p
                                        className="flex justify-content-between"
                                        style={{
                                          fontFamily: 'var(--fontBarlowMedium)',
                                          fontSize: 'var(--font-size-md)',
                                          color: 'var(--cardBackgroundColorOneShade80)',
                                        }}
                                      >
                                        {selectedLabel?.widgets.join(", ")}    
                                      </p>
                                    </div>
                                  ));
                        }}
                      />
                    </div>

                    <div className="mb-4">
                      <Controller
                        name="type"
                        control={control}
                        rules={TranslationsSchema(t).type}
                        render={({ field }) => {
                          return (
                            <>
                              {
                                !detail
                                ? <Dropdown
                                    className={`${
                                      errors.type?.message ? "p-invalid" : "dropdown"
                                    }`}
                                    label={t("Translations.typeLabel")}
                                    placeholder={t("Translations.typePlaceholderLabel")}
                                    options={TRANSLATION_LABELTYPE_OPTIONS(t).sort(
                                      (a, b) => a.label.localeCompare(b.label)
                                    )}
                                    filter={false}
                                    showClear={false}
                                    width="35rem"
                                    hasTooltipRef
                                    tooltipIdName="type-tooltip"
                                    tooltipRef={{
                                      content: t('Translations.typeTooltip'),
                                      position: "bottom",
                                    }}
                                    iconHasTooltip='iu iu-icon-info tooltip-dropdown-styles'
                                    {...field}
                                  />
                                : <CustomInput
                                    id={`input-label-type`}
                                    label={t("Translations.typeLabel")}
                                    type="text"
                                    className="input-md"
                                    hasTooltip
                                    tooltipText={t('Translations.typeTooltip')}
                                    isReadOnly
                                    {...field}
                                  />
                              }
                            </>
                          );
                        }}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="type"
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            className="p-error block"
                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    </div>

                    <div className={(update || detail) ? "" : "mb-4"}>
                      {/* {["key", "description"].map((key) => ( */}
                      {["key"].map((key) => (
                        <div key={key}>
                          <Controller
                            key={key}
                            name={key as any}
                            control={control}
                            rules={
                              // update && key === "description"
                              //   ? {} : (TranslationsSchema(t) as any)[key]
                              (TranslationsSchema(t) as any)[key]
                            }
                            render={({ field }) => {
                              return (
                                <CustomInput
                                  id={`input-label-${key}`}
                                  label={t(`Translations.${key}Label`)}
                                  placeholder={t(
                                    `Translations.${key}PlaceholderLabel`
                                  )}
                                  type="text"
                                  autoComplete="off"
                                  // @ts-ignore
                                  className={`${
                                    (errors as any)[key]?.message
                                      ? "p-invalid"
                                      : "input-md mb-4"
                                  }`}
                                  maxLength={key === "key" ? 60 : 512}
                                  keyfilter={
                                    key === "key"
                                      ? /^[A-Za-z0-9-_.]*$/
                                      : /^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]+$/
                                  }
                                  hasTooltip
                                  tooltipText={t(`Translations.${key}Tooltip`)}
                                  isReadOnly={detail}
                                  {...field}
                                />
                              );
                            }}
                          />
                          <ErrorMessage
                            key={`err-${key}`}
                            errors={errors}
                            name={key}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                }}
                              >
                                {message}
                              </small>
                            )}
                          />
                        </div>
                      ))}
                    </div>

                    {
                      (update || detail) &&
                      <div className="mb-4">
                        {["esvalue"].map((key) => (
                          <div key={key}>
                            <Controller
                              key={key}
                              name={key as any}
                              control={control}
                              rules={
                                (update || detail) && key === "description"
                                  ? {}
                                  : (TranslationsSchema(t) as any)[key]
                              }
                              render={({ field }) => {
                                return (
                                  <InputTextarea
                                    id={`input-label-${key}${detail ? '-readonly' : ''}`}
                                    label={t(`Translations.${key}Label`)}
                                    placeholder={t(
                                      `Translations.${key}PlaceholderLabel`
                                    )}
                                    autoComplete="off"
                                    // @ts-ignore
                                    className={`${
                                      (errors as any)[key]?.message
                                        ? "p-invalid"
                                        : "input-md"
                                    }`}
                                    maxLength={2048}
                                    hasTooltipIcon
                                    tooltipIconText={t(`Translations.${key}Tooltip`)}
                                    tooltipIconOptions={{ position: "bottom" }}
                                    autoResize
                                    rows={4}
                                    cols={30}
                                    style={{
                                      minHeight: "80px",
                                      maxHeight: "80px",
                                    }}
                                    readOnly={detail}
                                    {...field}
                                  />
                                );
                              }}
                            />
                            <ErrorMessage
                              key={`err-${key}`}
                              errors={errors}
                              name={key}
                              //@ts-ignore
                              render={({ message }) => (
                                <small
                                  className="p-error block"
                                  style={{
                                    marginTop: "0.5rem",
                                    marginBottom: "1.5rem",
                                  }}
                                >
                                  {message}
                                </small>
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    }
                  </div>

                  <div>
                    <div className="mb-4">
                      {((update || detail) ? ["envalue"] : ["esvalue", "envalue"]).map((key) => (
                        <div key={key}>
                          <Controller
                            key={key}
                            name={key as any}
                            control={control}
                            rules={
                              (update || detail) && key === "description"
                                ? {}
                                : (TranslationsSchema(t) as any)[key]
                            }
                            render={({ field }) => {
                              return (
                                <InputTextarea
                                  id={`input-label-${key}${detail ? '-readonly' : ''}`}
                                  label={t(`Translations.${key}Label`)}
                                  placeholder={t(
                                    `Translations.${key}PlaceholderLabel`
                                  )}
                                  autoComplete="off"
                                  // @ts-ignore
                                  className={`${
                                    (errors as any)[key]?.message
                                      ? "p-invalid"
                                      : `input-md${key === 'esvalue' ? ' mb-4' : ''}`
                                  }`}
                                  maxLength={2048}
                                  hasTooltipIcon
                                  tooltipIconText={t(`Translations.${key}Tooltip`)}
                                  tooltipIconOptions={{ position: "bottom" }}
                                  autoResize
                                  rows={4}
                                  cols={30}
                                  style={{
                                    minHeight: "80px",
                                    maxHeight: "80px",
                                  }}
                                  readOnly={detail}
                                  {...field}
                                />
                              );
                            }}
                          />
                          <ErrorMessage
                            key={`err-${key}`}
                            errors={errors}
                            name={key}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                }}
                              >
                                {message}
                              </small>
                            )}
                          />
                        </div>
                      ))}
                    </div>

                    <div>
                      <Controller
                        name="releaseversion"
                        control={control}
                        rules={TranslationsSchema(t).releaseversion}
                        render={({ field }) => {
                          return (update || detail) ? (
                            <CustomInput
                              id={`input-label-version`}
                              label={t(`Translations.releaseversionLabel`)}
                              placeholder={t(`Translations.versionPlaceholder`)}
                              type="text"
                              autoComplete="off"
                              // @ts-ignore
                              className="rd-only input-md mb-3"
                              maxLength={300}
                              {...field}
                              isReadOnly
                              hasTooltip
                              tooltipText={t(`Translations.releaseversionTooltip`)}
                            />
                          ) : (
                            // @ts-ignore
                            <Dropdown
                              className={`${
                                errors.releaseversion?.message ? "p-invalid" : "dropdown mb-4"
                              }`}
                              label={t("Translations.releaseversionLabel")}
                              placeholder={t("Translations.versionPlaceholder")}
                              options={versionsData
                                .map((version: any) => ({
                                  label: version.name,
                                  value: version.name,
                                }))
                                .sort((a: any, b: any) =>
                                  b.label.localeCompare(a.label)
                                )}
                              filter={false}
                              showClear={false}
                              width="35rem"
                              hasTooltipRef
                              tooltipIdName="releaseversion-tooltip"
                              tooltipRef={{
                                content: t('Translations.releaseversionTooltip'),
                                position: "bottom",
                              }}
                              iconHasTooltip='iu iu-icon-info tooltip-dropdown-styles'
                              {...field}
                            />
                          );
                        }}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="releaseversion"
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            className="p-error block"
                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    </div>

                    {
                      (update || detail) &&
                      ["createdat", "updatedat"].map((key) => (
                        <div className="mb-4">
                          <div
                              className="flex flex-column gap-2"
                              style={{ marginBottom: 0, width: '328px' }}
                          >
                              <label className="block ibp-input-label " htmlFor={`input-${key}`}>
                                  {t(`${key === 'createdat' ? 'Tenants' : 'Translations'}.${key}Label`)}
                              </label>
                              <p className="flex gap-2 align-items-center">
                                <i className="iu iu-icon-calendar" />
                                {/* @ts-ignore */}
                                {selectedLabel?.hasOwnProperty(key) ? formatDateToLocale(selectedLabel[key], language, true) : ''}
                              </p>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Implementación para la validación de Etiquetas */}
            {
              !detail &&
              <ToggleButton
                onLabel={t("Translations.validateSimilarLabelsButton")}
                onIcon=""
                checked={true}
                disabled={(!detail
                  ? (!isValid || (
                      (JSON.stringify(widgetsValues?.sort((a:any,b:any)=>a.name.localeCompare(b)))
                      === JSON.stringify(fieldsToValidateObject.widgets?.sort((a:any,b:any)=>a.name.localeCompare(b)))) &&
                      keyValues === fieldsToValidateObject.key
                  ))
                  : !isValid)
                  || (wasLabelValidated || !isDirty)
                }
                onChange={async ()=>{
                  setFieldsToValidateObject({
                    // @ts-ignore
                    widgets: !detail ? widgetsValues : [],
                    key: keyValues,
                  });
                  try {
                    const { data } = await validateLabel(selectedApp?.appid ?? '', getValues('key'));
                    setSimilarLabelsList(data);
                    setWasLabelValidated(true);
                    registerAudit({
                      role: selectedApp?.rolename,
                      group: "",
                      tenant: selectedApp?.tenantname,
                      environment: selectedApp?.env,
                      segment: selectedApp?.segment,
                      functionality: "LABELS_FRONT",
                      activity: "MATCH_LABELS_FRONT",
                      object: "LABEL",
                      initialValue: null,
                      finalValue: `{'key': ${getValues('key')}, 'widgets': ${
                        getValues('widgets')[0]?.hasOwnProperty('name')
                          ? getValues('widgets')?.map((item:any)=>item.name)
                          : getValues('widgets')
                      } }`,
                      status: "SUCCESS",
                      errorCode: "",
                      errorDescription: "",
                      errorCause: "",
                    });
                  } catch (error:any) {
                    toast.current.replace({
                      severity: "error",
                      summary: "",
                      detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
                      life: 3000,
                    });
                  }
                }}
                style={{
                  transform: 'translate(0,-15px)'
                }}
              />
              }

            {
              (!detail && !!similarLabelsList) &&
              (similarLabelsList?.length > 0
                ? <div>
                    <div className="flex justify-content-left w-full mt-2">
                      <p
                        className="boxTitle"
                        style={{width: '100%'}}>
                        {t("Translations.similarLabelsListTitle")}
                      </p>
                    </div>
                    <span
                      dangerouslySetInnerHTML={
                        { __html: t("Translations.similarLabelsListDescription") }}
                      className="block mt-2 mb-4 w-full"
                    />
                    <ValidateLabelsTable
                      data={similarLabelsList}
                      reloadFn={()=>{}}
                    />
                  </div>
                : <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      paddingTop: 'var(--padding-level-eight)',
                    }}
                  >
                    <img
                      src={noLabelsFoundedImage}
                      alt='no-labels-image'
                      style={{
                        maxHeight: '124px',
                      }}
                    />
                    <p
                      style={{
                        marginTop: 'var(--padding-level-sixteen)',
                        fontFamily: 'var(--fontBarlowSemiBold)',
                        fontSize: 'var(--font-size-md)',
                        color: 'var(--cardBackgroundColorOneShade80)'
                      }}
                    >
                      {t('Translations.noLabelsFoundedDescription')}
                    </p>
                  </div>)
            }

            <div className="form-btngroup-footer my-5 p-0 gap-6">
              <PrivilegeValidatorComponent showButton={!(detail && (!permissiondInfoByRole.REMOVE || !selectedLabel?.isCustom))} >
                <Button
                  variant="p-button-link"
                  label={t(`Translations.${detail ? 'delete': 'cancel'}BtnLabel`)}
                  onClick={detail
                    ? () => handleModalProps("action")
                    : (isDirty ? () => handleModalProps("cancel") : onReturn)
                  }
                />
              </PrivilegeValidatorComponent>
              {
                (((detail || update) && permissiondInfoByRole.UPDATE)
                || (!detail && !update && permissiondInfoByRole.CREATE)) &&
                <Button
                variant="p-button"
                label={
                  update
                    ? t("Translations.editBtnLabel")
                    : (detail
                        ? t("Translations.editFromDetailBtnLabel")
                        : t("Translations.createBtnLabel"))
                }
                disabled={(!detail && (
                    (JSON.stringify(widgetsValues?.sort((a:any,b:any)=>a.name.localeCompare(b)))
                    !== JSON.stringify(fieldsToValidateObject.widgets?.sort((a:any,b:any)=>a.name.localeCompare(b)))) ||
                    keyValues !== fieldsToValidateObject.key))
                    ? !wasLabelValidated
                    : update ? !(isValid && isDirty) : !isValid
                }
                onClick={!detail
                  ? () => handleModalProps("action")
                  : () => {
                    setValue('widgets',selectedLabel?.widgets.map((widget) => ({
                      name: widget,
                      code: widget,
                    })) || []);
                    setValue('type', selectedLabel?.type);
                    navigate('/translations/edit');
                  }
                }
              />
              }
            </div>
          </BoxFull>
        </>
      ) : (
        <BoxFull className="dt-c my-4">
          <FormSkeleton length={8} />
        </BoxFull>
      )}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
